import React, { useEffect, useState } from 'react';
import { ShieldCheck, ShieldOff, AlertTriangle, RefreshCw, Users } from 'lucide-react';
import {
  contactGateService,
  type ContactTouchPolicy,
  type GatePreview,
} from 'api/services/contactGate.service';

/**
 * The cross-channel contact ceiling: how often one person may be reached, at all,
 * across every channel.
 *
 * <h3>Why the preview leads and the switch follows</h3>
 * Each campaign already has its own `suppress_recent_days`, and that is exactly
 * the problem — five campaigns each honouring their own window still stack five
 * messages onto the same person, and none of them can see the WhatsApps or calls
 * from the same week. This ceiling counts every channel together.
 *
 * <p>It is off for every organization until somebody turns it on, and the number
 * has to be chosen from evidence. Set it too low on live campaigns and the
 * failure is SILENT under-sending, which nobody notices for days. So the page
 * answers "what would this have blocked over the history we actually have"
 * before it offers the switch.
 */
const NUDGE = 'text-sm text-gray-600 dark:text-gray-400';

const ContactGate: React.FC = () => {
  const [policy, setPolicy] = useState<ContactTouchPolicy | null>(null);
  const [preview, setPreview] = useState<GatePreview | null>(null);

  const [maxTouches, setMaxTouches] = useState(3);
  const [windowDays, setWindowDays] = useState(7);
  const [enabled, setEnabled] = useState(false);

  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const res = await contactGateService.getPolicy();
      if (!alive) return;
      if (res.success && res.data) {
        setPolicy(res.data);
        setEnabled(res.data.enabled);
        // Fall back to the server's own defaults rather than inventing numbers,
        // so an unconfigured organization starts where the backend would.
        if (res.data.maxTouches) setMaxTouches(res.data.maxTouches);
        if (res.data.windowDays) setWindowDays(res.data.windowDays);
      } else {
        setNote({ kind: 'err', text: res.error || 'Could not load the current setting.' });
      }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  const runPreview = async () => {
    setPreviewing(true);
    setNote(null);
    const res = await contactGateService.preview(maxTouches, windowDays);
    if (res.success && res.data) setPreview(res.data);
    else setNote({ kind: 'err', text: res.error || 'Could not work out what this would block.' });
    setPreviewing(false);
  };

  const save = async () => {
    setSaving(true);
    setNote(null);
    const res = await contactGateService.setPolicy({ enabled, maxTouches, windowDays });
    if (res.success && res.data) {
      setPolicy(res.data);
      setNote({
        kind: 'ok',
        text: res.data.enabled
          ? `Ceiling on: at most ${res.data.maxTouches} messages to one person in ${res.data.windowDays} days, across all channels.`
          : 'Ceiling off. Nobody will be blocked by it.',
      });
    } else {
      setNote({ kind: 'err', text: res.error || 'Could not save.' });
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="p-6 text-gray-600 dark:text-gray-400">Loading…</div>;
  }

  const live = policy?.enabled === true;

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <header>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Contact ceiling</h1>
        <p className={`${NUDGE} mt-1`}>
          The most times one person may be contacted — counting email, WhatsApp and calls
          together. Separate from each campaign's own recency window, which only ever sees
          its own history.
        </p>
      </header>

      {/* Current state, said plainly. "No ceiling" is the norm, not an error. */}
      <div className={`flex items-start gap-3 rounded-lg border p-4 ${
        live
          ? 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
          : 'border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
      }`}>
        {live
          ? <ShieldCheck className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" />
          : <ShieldOff className="w-5 h-5 text-gray-500 mt-0.5" />}
        <div>
          <p className="font-medium text-gray-900 dark:text-gray-100">
            {live
              ? `On — at most ${policy?.maxTouches} in ${policy?.windowDays} days`
              : 'Off — nobody is being blocked by a ceiling'}
          </p>
          {policy?.updatedBy && (
            <p className={NUDGE}>
              Last changed by {policy.updatedBy}
              {policy.updatedAt ? ` on ${new Date(policy.updatedAt).toLocaleDateString()}` : ''}
            </p>
          )}
        </div>
      </div>

      {/* ── Step 1: what would it block ─────────────────────────────────── */}
      <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-5 space-y-4">
        <div>
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">
            First, see what it would have stopped
          </h2>
          <p className={NUDGE}>
            Counted from what was actually delivered, on every channel. Nothing is changed by
            looking.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Most messages per person
            </span>
            <input
              type="number" min={1} value={maxTouches}
              onChange={(e) => setMaxTouches(Math.max(1, Number(e.target.value) || 1))}
              className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-gray-100"
            />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Over how many days
            </span>
            <input
              type="number" min={1} max={365} value={windowDays}
              onChange={(e) => setWindowDays(Math.min(365, Math.max(1, Number(e.target.value) || 1)))}
              className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-gray-100"
            />
          </label>
          <button
            onClick={runPreview} disabled={previewing}
            className="tl-btn tl-btn-primary disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${previewing ? 'animate-spin' : ''}`} />
            {previewing ? 'Working…' : 'Check'}
          </button>
        </div>

        {preview && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4">
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {preview.peopleOverTheLimit.toLocaleString()}
                </p>
                <p className={NUDGE}>
                  people would be blocked right now
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4">
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {preview.totalSentInWindow.toLocaleString()}
                </p>
                <p className={NUDGE}>
                  messages delivered in the last {preview.windowDays} days
                </p>
              </div>
            </div>

            {preview.totalSentInWindow === 0 && (
              <p className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                {/* Zero history is not zero risk — say so rather than let an empty
                    result read as "this ceiling is safe". */}
                Nothing has been delivered in this window yet, so this check cannot tell you
                what the ceiling would do. Turning it on now would be a guess.
              </p>
            )}

            {preview.busiest.length > 0 && (
              <div>
                <p className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Users className="w-4 h-4" />
                  Most contacted
                  {preview.peopleOverTheLimit > preview.busiest.length && (
                    <span className={NUDGE}>
                      (showing {preview.busiest.length} of {preview.peopleOverTheLimit.toLocaleString()})
                    </span>
                  )}
                </p>
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Contact</th>
                        <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Messages</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.busiest.map((r) => (
                        <tr key={r.matchKey} className="border-t border-gray-200 dark:border-gray-700">
                          <td className="px-4 py-2 font-mono text-gray-900 dark:text-gray-100">{r.matchKey}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-900 dark:text-gray-100">{r.touches}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── Step 2: turn it on ──────────────────────────────────────────── */}
      <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-5 space-y-4">
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Then turn it on</h2>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox" checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="mt-1 w-4 h-4"
          />
          <span>
            <span className="font-medium text-gray-900 dark:text-gray-100">
              Enforce this ceiling
            </span>
            <span className={`block ${NUDGE}`}>
              Anyone already at the limit is skipped, and the reason is recorded against
              them. Do-not-contact entries are honoured whether this is on or off.
            </span>
          </span>
        </label>

        {enabled && !preview && (
          <p className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            You have not checked what this would block. If the number is too low the effect
            is messages quietly not being sent — worth looking first.
          </p>
        )}

        <div className="flex items-center gap-3">
          <button
            onClick={save} disabled={saving}
            className="tl-btn tl-btn-primary disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {note && (
            <span className={`text-sm ${
              note.kind === 'ok'
                ? 'text-green-700 dark:text-green-400'
                : 'text-red-700 dark:text-red-400'
            }`}>
              {note.text}
            </span>
          )}
        </div>
      </section>
    </div>
  );
};

export default ContactGate;
